import { useState } from "react";
import { Form, Row, Col, Button } from "react-bootstrap";
import DialogFooter from "./DialogFooter.js";
import { useSelector } from "react-redux";
import { buildDashboardModule, buildDashboardPayload } from "./dashboardPayloadConstants.js";

const CreateJobsView = ({
  initialSettings = {},
  onHide,
  isUpdateMode = false,
  jobsCount,
  reloadDashboard,
  toastRef,
}) => {
  // Redux user info
  const { userInformation } = useSelector((state) => state.UserReducer);
  const defaultFromOptions = [
    { label: "My Tasks", value: "My Tasks", checked: false },
    { label: "My Projects", value: "My Projects", checked: false },
    { label: "All Projects", value: "All Projects", checked: false },
  ];

  const defaultJobStatusOptions = [
    { label: "Awaiting", value: "Awaiting", checked: false },
    { label: "In-Progress", value: "In-Progress", checked: false },
    { label: "Complete-Approved", value: "Complete-Approved", checked: false },
    { label: "Complete-Rejected", value: "Complete-Rejected", checked: false },
  ];

  const defaultJobTypeOptions = [
    { label: "CIC/CICM", value: "CIC/CICM", checked: false },
    { label: "RDT", value: "RDT", checked: false },
  ];
  // Parse initial From values, handle missing From gracefully
  const [submitting, setSubmitting] = useState(false);
  const initialFrom = initialSettings?.From
    ? initialSettings.From.split(",").map((f) => f.trim())
    : [];
  const [fromOptions, setFromOptions] = useState(
    defaultFromOptions.map((opt) => ({
      ...opt,
      checked: initialFrom.includes(opt.value),
    })),
  );

  // Parse initial JobType values
  const initialJobType = initialSettings?.JobType
    ? initialSettings?.JobType.split(",").map((f) => f.trim())
    : [];
  const [jobTypeOptions, setJobTypeOptions] = useState(
    defaultJobTypeOptions.map((opt) => ({
      ...opt,
      checked: initialJobType.includes(opt.value),
    })),
  );

  // Parse initial JobStatus values
  const initialJobStatus = initialSettings?.JobStatus
    ? initialSettings?.JobStatus.split(",").map((f) => f.trim())
    : [];
  const [jobStatusOptions, setJobStatusOptions] = useState(
    defaultJobStatusOptions.map((opt) => ({
      ...opt,
      checked: initialJobStatus.includes(opt.value),
    })),
  );

  // ...existing code for job type/status state...

  const handleFromCheckboxChange = (index) => {
    const updatedOptions = [...fromOptions];
    updatedOptions[index].checked = !updatedOptions[index].checked;
    setFromOptions(updatedOptions);
  };

  const handleJobTypeCheckboxChange = (index) => {
    const updatedOptions = [...jobTypeOptions];
    updatedOptions[index].checked = !updatedOptions[index].checked;
    setJobTypeOptions(updatedOptions);
  };

  const handleJobStatusCheckboxChange = (index) => {
    const updatedOptions = [...jobStatusOptions];
    updatedOptions[index].checked = !updatedOptions[index].checked;
    setJobStatusOptions(updatedOptions);
  };

  const buildDashboardJobPayload = () => {
    // Extract checked values for each option type
    const getChecked = (options) =>
      options
        .filter((opt) => opt.checked)
        .map((opt) => opt.value.trim())
        .join(",");

    const userId = initialSettings?.User_ID || userInformation?.userid;
    // Handle jobsCount undefined or NaN
    let moduleName;
    if (isUpdateMode) {
      moduleName = initialSettings?.Title;
    } else {
      const count = typeof jobsCount === "number" && !isNaN(jobsCount) ? jobsCount : 0;
      moduleName = `Jobs_${count + 1}`;
    }
    const module = buildDashboardModule({
      moduleId: isUpdateMode ? initialSettings?.Module_ID || "" : "",
      moduleName: moduleName,
      moduleType: "Jobs",
      userId,
      config: {
        From: getChecked(fromOptions),
        JobType: getChecked(jobTypeOptions),
        JobStatus: getChecked(jobStatusOptions),
      },
      Sequence: isUpdateMode ? initialSettings?.Sequence : -1
    });
    return buildDashboardPayload({ module });
  };

  const handleCreateOrUpdate = async () => {
    setSubmitting(true);
    const payload = buildDashboardJobPayload();
    try {
      // Import createOrUpdateDashboardModuleView dynamically to avoid circular deps
      const { createOrUpdateDashboardModuleView } =
        await import("../../../apis/dashboardApi.js");
      await createOrUpdateDashboardModuleView(payload);
      if (toastRef && toastRef.current) {
        toastRef.current.show({
          severity: "success",
          summary: "Success",
          detail: isUpdateMode
            ? "Jobs module updated successfully"
            : "Jobs module created successfully",
          life: 3000,
        });
      }
      if (typeof reloadDashboard === "function") reloadDashboard();
      onHide();
    } catch (err) {
      if (toastRef && toastRef.current) {
        toastRef.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to create/update jobs module.",
          life: 5000,
        });
      }
      console.error("Error creating/updating jobs module:", err);
      setSubmitting(false);
    }
  };

  const isFormValid =
    fromOptions.some((option) => option.checked) &&
    jobTypeOptions.some((option) => option.checked) &&
    jobStatusOptions.some((option) => option.checked);

  return (
    <>
      <Row className="fullkit-form">
        <Col md={4}>
          <Form.Group>
            <Form.Label className="mb-2 module-form-label" style={{ paddingLeft: "20px" }}>
              From <span className="mandatory-asterisk"> *</span>
            </Form.Label>
            <div className="d-flex flex-column gap-2 ms-1" style={{ paddingLeft: "20px" }}>
              {fromOptions.map((option, index) => (
                <Form.Check
                  key={option.value}
                  type="checkbox"
                  id={`from-${option.value}`}
                  label={option.label}
                  checked={option.checked}
                  onChange={() => handleFromCheckboxChange(index)}
                />
              ))}
            </div>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label className="mb-2 module-form-label" style={{ paddingLeft: "20px" }}>
              Job Type <span className="mandatory-asterisk"> *</span>
            </Form.Label>
            <div className="d-flex flex-column gap-2 ms-1" style={{ paddingLeft: "20px" }}>
              {jobTypeOptions.map((option, index) => (
                <Form.Check
                  key={option.value}
                  type="checkbox"
                  id={`jobType-${option.value}`}
                  label={option.label}
                  checked={option.checked}
                  onChange={() => handleJobTypeCheckboxChange(index)}
                />
              ))}
            </div>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label className="mb-2 module-form-label" style={{ paddingLeft: "20px" }}>
              Job Status <span className="mandatory-asterisk"> *</span>
            </Form.Label>
            <div className="d-flex flex-column gap-2 ms-1" style={{ paddingLeft: "20px" }}>
              {jobStatusOptions.map((option, index) => (
                <Form.Check
                  key={option.value}
                  type="checkbox"
                  id={`jobStatus-${option.value}`}
                  label={option.label}
                  checked={option.checked}
                  onChange={() => handleJobStatusCheckboxChange(index)}
                />
              ))}
            </div>
          </Form.Group>
        </Col>
      </Row>

      <DialogFooter
        submitting={submitting}
        isFormValid={isFormValid}
        isUpdateMode={isUpdateMode}
        onCancel={onHide}
        onSubmit={handleCreateOrUpdate}
      />
    </>
  );
};

export default CreateJobsView;
